import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { websiteShares } from "../api/endpoints";

/**
 * Project sharing modal — owner-only.
 *
 * Lists every collaborator currently on the website and lets the owner
 * invite a registered user by email + role. Roles:
 *   - editor    → full write access (prompts / schedule / runs)
 *   - read_only → dashboard + reports only
 *
 * Inviting an unknown email returns 404 from the API; we surface a
 * tailored error so the user knows the invitee must register first.
 * Removing a row revokes access immediately on the next request.
 */
export default function ShareWebsiteModal({ website, onClose }) {
  const { t } = useTranslation();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", role: "editor" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const r = await websiteShares.list(website.id);
      setShares(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      // Likely 403 because the caller isn't the owner — closing the
      // modal here would be confusing; instead show an inline error.
      setError(err?.response?.data?.detail || t("sharing.couldNotLoad"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [website.id]);

  async function invite(e) {
    e.preventDefault();
    if (busy || !form.email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await websiteShares.invite(website.id, {
        email: form.email.trim(),
        role: form.role,
      });
      setForm({ email: "", role: form.role });
      await reload();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) {
        setError(detail || t("sharing.userNotRegistered"));
      } else {
        setError(
          detail ||
            JSON.stringify(err?.response?.data) ||
            t("sharing.couldNotInvite"),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function revoke(share) {
    if (!confirm(t("sharing.removeConfirm", { email: share.email }))) return;
    try {
      await websiteShares.remove(website.id, share.id);
      await reload();
    } catch (err) {
      setError(err?.response?.data?.detail || t("sharing.couldNotRemove"));
    }
  }

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-20 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-5">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {t("sharing.title")}
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {t("sharing.subtitle", { label: website.label })}
          </p>
        </div>

        <form onSubmit={invite} className="space-y-3">
          <div className="grid gap-2">
            <input
              type="email"
              required
              autoComplete="email"
              placeholder={t("sharing.emailPlaceholder")}
              className="input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <select
              className="input"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="editor">{t("sharing.roleEditor")}</option>
              <option value="read_only">{t("sharing.roleReadOnly")}</option>
            </select>
            <button className="btn-primary" disabled={busy}>
              {busy ? t("sharing.inviting") : t("sharing.invite")}
            </button>
          </div>
          <p className="text-xs text-slate-500">{t("sharing.invitedHint")}</p>
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {error}
            </div>
          )}
        </form>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            {t("sharing.membersHeading")}
          </div>
          {loading ? (
            <div className="text-sm text-slate-500">{t("common.loading")}</div>
          ) : shares.length === 0 ? (
            <div className="text-sm text-slate-500">{t("sharing.empty")}</div>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
              {shares.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="text-slate-900 truncate">{s.email}</div>
                    {(s.first_name || s.last_name) && (
                      <div className="text-xs text-slate-500 truncate">
                        {s.first_name} {s.last_name}
                      </div>
                    )}
                  </div>
                  <span
                    className={[
                      "text-xs rounded-full px-2 py-0.5 ring-1",
                      s.role === "editor"
                        ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
                        : "bg-slate-100 text-slate-700 ring-slate-200",
                    ].join(" ")}
                  >
                    {t(`sharing.role_${s.role}`)}
                  </span>
                  <button
                    type="button"
                    onClick={() => revoke(s)}
                    className="text-xs text-red-600 hover:underline whitespace-nowrap"
                  >
                    {t("sharing.remove")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <button onClick={onClose} className="btn-ghost">
            {t("common.close", "Close")}
          </button>
        </div>
      </div>
    </div>
  );
}
